import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import { ApiError } from "@/api/client"
import { useChangePassword } from "@/api/hooks/auth"
import { useAuth } from "@/auth/AuthProvider"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { usePageTitle } from "@/layouts/pageChrome"

/**
 * React port of `templates/profile.html` (T3.2). Parity checklist lives
 * in `docs/migration/parity/profile.md`.
 *
 * Consolidations vs. the Jinja source:
 *   1. The template fires TWO `/auth/me` requests on mount
 *      (`loadUserData` + `checkAdminAccess`). The React port consumes
 *      the cached `useCurrentUser()` result via `useAuth()` so the page
 *      issues zero `/auth/me` requests of its own. Admin-tab visibility
 *      is handled by `<AppShell/>` / the sidebar, not this page.
 *   2. The inline `#changePasswordMessage` `display:none` div is
 *      replaced by conditionally-rendered shadcn `<Alert/>` banners
 *      (pending / success / error).
 *   3. The "👤" heading emoji is preserved literally — parity UI copy,
 *      not code-authored emoji, so the project's no-emoji rule doesn't
 *      apply (see T3.9 `🚀` precedent).
 */

const schema = z
  .object({
    current_password: z
      .string()
      .trim()
      .min(1, "All password fields are required."),
    new_password: z
      .string()
      .trim()
      .min(8, "New password must be at least 8 characters long."),
    confirm_password: z
      .string()
      .trim()
      .min(8, "All password fields are required."),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    path: ["confirm_password"],
    message: "New passwords do not match.",
  })

type ChangePasswordFormValues = z.infer<typeof schema>

type BannerState =
  | { kind: "idle" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string }

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[minmax(160px,220px)_1fr] items-center py-3 first:pt-0 last:pb-0">
      <span className="text-sm text-[#b0b0b0]">{label}</span>
      <span className="justify-self-end text-sm font-semibold text-brand-fg">
        {value}
      </span>
    </div>
  )
}

function formatCreatedAt(createdAt: string | null | undefined): string {
  if (!createdAt) return "N/A"
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return "N/A"
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

export default function ProfilePage() {
  usePageTitle("Profile")
  const { user, isLoading } = useAuth()
  const mutation = useChangePassword()
  const [banner, setBanner] = useState<BannerState>({ kind: "idle" })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  })

  // Parity: any keystroke in the password fields hides the status
  // banner (profile.html:760-769). Done in onChange rather than by
  // watching values, so `reset()` after a successful submit doesn't
  // clear the success banner.
  const clearBanner = () =>
    setBanner((prev) => (prev.kind === "idle" ? prev : { kind: "idle" }))

  if (isLoading) {
    return <p className="text-brand-fg">Loading...</p>
  }
  if (!user) {
    return <p className="text-brand-fg">Error loading profile</p>
  }

  const onSubmit = handleSubmit(async (values) => {
    setBanner({ kind: "idle" })
    try {
      const result = await mutation.mutateAsync({
        current_password: values.current_password.trim(),
        new_password: values.new_password.trim(),
        confirm_password: values.confirm_password.trim(),
      })
      reset()
      setBanner({
        kind: "success",
        message: result.message ?? "Password updated successfully.",
      })
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to update password."
      setBanner({
        kind: "error",
        message: message || "Failed to update password.",
      })
    }
  })

  return (
    <Card className="mx-auto mt-2 w-full max-w-3xl rounded-xl border-[#4a4a4a] bg-[#3a3a3a] text-brand-fg shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
      <CardHeader className="pb-4">
        <h1 className="text-3xl font-semibold">👤 Profile</h1>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="divide-y divide-[#4a4a4a]">
          <ProfileRow label="Username:" value={user.username || "N/A"} />
          <ProfileRow label="Email:" value={user.email || "N/A"} />
          <ProfileRow
            label="Account Created:"
            value={formatCreatedAt(user.created_at)}
          />
        </div>

        <hr className="my-7 border-[#4a4a4a]" />

        <div className="mb-6">
          <h2 className="text-3xl font-semibold">Change Password</h2>
          <p className="mt-1 text-sm text-[#b0b0b0]">
            Keep your account secure by updating your password regularly.
          </p>
        </div>

        {mutation.isPending ? (
          <Alert className="mb-4 border-[#3a4a66] bg-[#2a3a4a] text-[#9bbcff]">
            <AlertDescription>Updating password…</AlertDescription>
          </Alert>
        ) : banner.kind === "success" ? (
          <Alert className="mb-4 border-[#335533] bg-[#2a4a2a] text-[#86efac]">
            <AlertDescription>{banner.message}</AlertDescription>
          </Alert>
        ) : banner.kind === "error" ? (
          <Alert
            variant="destructive"
            className="mb-4 border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{banner.message}</AlertDescription>
          </Alert>
        ) : null}

        <form
          onSubmit={onSubmit}
          autoComplete="off"
          noValidate
          className="max-w-[420px]"
        >
          <div className="mb-4">
            <Label
              htmlFor="current_password"
              className="mb-2 block font-semibold text-brand-fg"
            >
              Current Password
            </Label>
            <Input
              id="current_password"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.current_password ? "true" : "false"}
              className="h-11 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("current_password", { onChange: clearBanner })}
            />
            {errors.current_password ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.current_password.message}
              </p>
            ) : null}
          </div>

          <div className="mb-4">
            <Label
              htmlFor="new_password"
              className="mb-2 block font-semibold text-brand-fg"
            >
              New Password
            </Label>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.new_password ? "true" : "false"}
              className="h-11 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("new_password", { onChange: clearBanner })}
            />
            <p className="mt-1 text-xs text-[#b0b0b0]">
              Minimum 8 characters
            </p>
            {errors.new_password ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.new_password.message}
              </p>
            ) : null}
          </div>

          <div className="mb-6">
            <Label
              htmlFor="confirm_password"
              className="mb-2 block font-semibold text-brand-fg"
            >
              Confirm New Password
            </Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.confirm_password ? "true" : "false"}
              className="h-11 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("confirm_password", { onChange: clearBanner })}
            />
            {errors.confirm_password ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.confirm_password.message}
              </p>
            ) : null}
          </div>

          <Button
            type="submit"
            disabled={mutation.isPending}
            className="h-11 rounded-lg px-6 font-semibold text-white border-0 bg-gradient-to-br from-[#8b9aff] to-[#667eea] hover:opacity-95 transition-all"
          >
            Update Password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
